import type { PlanActionAvailability } from '@/api/gen/_model/planActionAvailability';
import { type CommonTranslationKey } from '@/types/translation';

/**
 * 현재(활성) 플랜 연장의 차단 판정.
 *
 * 미래 플랜 연장은 `getFuturePlanExtensionAvailability` 가 담당한다. 이 파일은
 * 그 대칭인 활성 플랜 쪽을 맡는다.
 *
 * 판정 권위는 서버의 `actions.canExtend` 하나다. `extendBlockedReason` 을 나열해
 * 걸러내지 않는 이유는, 플랜 관리 화면이 이미 `canExtend` 를 권위로 삼아 버튼
 * 노출을 정하기 때문이다(`PlanOverview` 의 `canShowExtendAction`). 사유가 늘어도
 * 여기가 자동으로 덮는다. 사유는 안내 문구를 고르는 데만 쓴다.
 */
export type CurrentExtensionBlock =
  | { blocked: false }
  | { blocked: true; reason: PlanActionAvailability['extendBlockedReason'] };

/**
 * 판정에 실제로 필요한 최소 필드만 받는다. 플랜 관리 화면은 `PlanActionAvailability`
 * 전체가 아니라 좁힌 뷰모델을 들고 있어서, 전체 타입을 요구하면 호출부가 막힌다.
 */
export type ExtensionActionAvailability = Pick<
  PlanActionAvailability,
  'canExtend' | 'extendBlockedReason'
>;

const NOT_BLOCKED: CurrentExtensionBlock = { blocked: false };

/**
 * `actions` 가 없으면 차단하지 않는다. 플랜 관리 응답을 아직 못 받은 상태까지
 * 막으면 정상 사용자가 연장을 못 하게 되므로, 그 경우는 서버 판정에 위임한다.
 */
export const resolveCurrentExtensionBlock = (
  actions: ExtensionActionAvailability | undefined,
): CurrentExtensionBlock =>
  actions?.canExtend === false
    ? { blocked: true, reason: actions.extendBlockedReason }
    : NOT_BLOCKED;

/**
 * 연장 차단 안내에 쓸 i18n 키. 진입점(업셀링 모달·플랜 관리 화면)이 같은 문구를
 * 쓰도록 여기서 한 번만 고른다.
 *
 * 반드시 `common` 네임스페이스를 쓴다. 이 문구는 업셀링 모달에서도 뜨는데, 모달은
 * 헤더·사이드바·paywall 등 어디서든 열리고 `common` 외 네임스페이스는 페이지가
 * `serverSideTranslations` 에 직접 명시해야 로드된다. `billing` 은 `/profile/plan`
 * 과 `/checkout*` 에서만 로드되므로, 그 밖의 페이지에서는 에러 없이 키 문자열이
 * 그대로 화면에 노출된다 — 빌드·타입체크·테스트로는 잡히지 않는 종류다.
 */
const EXTENSION_BLOCKED_COPY = {
  title: 'common:planManage.extensionBlocked.title',
  tooCloseDescription: 'common:planManage.extensionBlocked.description',
  unavailableDescription: 'common:planManage.extensionBlocked.unavailable',
} as const satisfies Record<string, CommonTranslationKey>;

export const getExtensionBlockedMessageKeys = (
  reason: PlanActionAvailability['extendBlockedReason'],
) => {
  if (reason === 'FUTURE_PLAN_TOO_CLOSE') {
    return {
      title: EXTENSION_BLOCKED_COPY.title,
      description: EXTENSION_BLOCKED_COPY.tooCloseDescription,
    } as const;
  }

  return {
    title: EXTENSION_BLOCKED_COPY.title,
    description: EXTENSION_BLOCKED_COPY.unavailableDescription,
  } as const;
};
